import { useEffect, useState } from "react";
import { api } from "./utils";
import { WhatsappLogo, Warning, CheckCircle, Clock, XCircle, ArrowsClockwise } from "@phosphor-icons/react";

const Pill = ({ tone = "neutral", children, testid }) => {
  const map = {
    ok:   "bg-[#4ECDC4]/30 border-[#4ECDC4] text-[#0F5F5A]",
    warn: "bg-[#FFD166]/60 border-[#111]/40 text-[#111]",
    bad:  "bg-[#FF6B6B]/25 border-[#FF6B6B] text-[#9B1E1E]",
    neutral: "bg-white border-black/15 text-[#111]",
  };
  return (
    <span data-testid={testid} className={`text-[10px] font-bold uppercase tracking-widest border rounded px-2 py-0.5 ${map[tone]}`}>
      {children}
    </span>
  );
};

const qualityTone = (q) => q === "GREEN" ? "ok" : q === "YELLOW" ? "warn" : q === "RED" ? "bad" : "neutral";

export default function WhatsAppHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/whatsapp-health");
      setData(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const s = data?.stats_24h || {};
  const p = data?.phone || {};
  const t = data?.token || {};
  const tpl = data?.templates || {};
  const attemptRate = s.attempted > 0 ? Math.round((s.sent / s.attempted) * 100) : null;

  return (
    <div className="nb-card p-4 sm:p-5 space-y-4" data-testid="whatsapp-health-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md border-2 border-black bg-[#25D366] flex items-center justify-center">
            <WhatsappLogo size={18} weight="bold" color="#FFFFFF" />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#4A4A4A]">Owner Console</p>
            <h3 className="font-heading text-lg font-black tracking-tight leading-none">WhatsApp Health</h3>
          </div>
        </div>
        <button
          onClick={load}
          data-testid="wa-health-refresh"
          className="nb-btn bg-white text-xs px-2 py-1.5 flex items-center gap-1"
          disabled={loading}
          title="Refresh"
        >
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {err && <div className="nb-card p-2 bg-[#FF6B6B] text-white text-xs font-bold">{err}</div>}
      {loading && !data && <p className="text-sm text-[#4A4A4A]">Loading live status from Meta…</p>}

      {data && !data.configured && (
        <div className="nb-card p-3 bg-[#FFD166]/50 text-xs font-semibold">
          META_ACCESS_TOKEN or META_PHONE_NUMBER_ID is missing from backend/.env — WhatsApp sending is in mock mode.
        </div>
      )}

      {data && data.configured && (
        <>
          {/* Row 1: Token + Phone */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="nb-card p-3 bg-[#FDFBF7]" data-testid="wa-health-token">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Access Token</p>
                {t.valid ? <Pill tone="ok" testid="token-status">Valid</Pill> : <Pill tone="bad">Invalid</Pill>}
              </div>
              <div className="space-y-1 text-xs">
                <p><span className="text-[#4A4A4A]">App:</span> <b>{t.app_name || "—"}</b></p>
                <p><span className="text-[#4A4A4A]">Type:</span> <b>{t.type || "—"}</b></p>
                <p><span className="text-[#4A4A4A]">Expires:</span>{" "}
                  <b className={t.expires_at === "never" ? "text-[#0F5F5A]" : ""}>{t.expires_at || "—"}</b>
                </p>
                <p className="text-[#4A4A4A] leading-tight pt-1">
                  Scopes: {(t.scopes || []).length ? t.scopes.join(", ") : "—"}
                </p>
              </div>
            </div>

            <div className="nb-card p-3 bg-[#FDFBF7]" data-testid="wa-health-phone">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Business Phone</p>
                <Pill tone={qualityTone(p.quality_rating)} testid="phone-quality">
                  {p.quality_rating || "—"}
                </Pill>
              </div>
              <div className="space-y-1 text-xs">
                <p className="font-heading font-black text-base">{p.display_phone_number || "—"}</p>
                <p><span className="text-[#4A4A4A]">Display name:</span> <b>{p.verified_name || "—"}</b>{" "}
                  {p.name_status === "APPROVED" && <Pill tone="ok">Approved</Pill>}
                  {p.name_status && p.name_status !== "APPROVED" && <Pill tone="warn">{p.name_status}</Pill>}
                </p>
                <p><span className="text-[#4A4A4A]">Tier:</span> <b>{p.messaging_limit_tier || "TIER_?"}</b></p>
                <p><span className="text-[#4A4A4A]">Platform:</span> <b>{p.platform_type || "—"}</b></p>
              </div>
            </div>
          </div>

          {/* Row 2: 24h stats */}
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Last 24h Sends</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="wa-health-stats">
              <Stat label="Attempted" value={s.attempted ?? 0} />
              <Stat label="Sent to Meta" value={s.sent ?? 0} tone="ok" />
              <Stat label="Delivered" value={s.delivered ?? 0} tone="ok" />
              <Stat label="Read" value={s.read ?? 0} tone="ok" />
              <Stat label="Failed" value={s.failed ?? 0} tone={s.failed > 0 ? "bad" : "neutral"} />
            </div>
            {attemptRate !== null && (
              <p className="text-[11px] text-[#4A4A4A] mt-1.5 font-semibold">
                Meta acceptance rate: <b>{attemptRate}%</b>
              </p>
            )}
          </div>

          {/* Row 3: Templates */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Approved Templates</p>
              <div className="flex gap-1">
                <Pill tone="ok" testid="tpl-approved">{tpl.approved || 0} approved</Pill>
                <Pill tone="warn">{tpl.pending || 0} pending</Pill>
                <Pill tone="bad">{tpl.rejected || 0} rejected</Pill>
              </div>
            </div>
            {(tpl.items || []).length === 0 ? (
              <div className="nb-card p-3 bg-[#FFD166]/40 text-xs font-semibold">
                Template list unavailable. Set <code className="font-mono">META_WABA_ID</code> in <code className="font-mono">backend/.env</code> to enable live template status listing. Find your WABA ID in WhatsApp Manager → Overview → the ID under your business name.
              </div>
            ) : (
              <ul className="space-y-1" data-testid="wa-health-templates">
                {tpl.items.map((it) => (
                  <li key={`${it.name}-${it.language}`} className="flex items-center justify-between text-xs border-b border-black/10 py-1.5">
                    <div className="min-w-0">
                      <p className="font-bold font-mono truncate">{it.name}</p>
                      <p className="text-[10px] text-[#4A4A4A] uppercase">{it.category} · {it.language}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {it.status === "APPROVED" && <><CheckCircle size={14} weight="bold" className="text-[#4ECDC4]" /><span className="text-[10px] font-bold uppercase">Approved</span></>}
                      {(it.status === "PENDING" || it.status === "IN_APPEAL") && <><Clock size={14} weight="bold" className="text-[#FFD166]" /><span className="text-[10px] font-bold uppercase">{it.status}</span></>}
                      {it.status === "REJECTED" && (
                        <span title={it.rejected_reason || "Rejected"} className="flex items-center gap-1">
                          <XCircle size={14} weight="bold" className="text-[#FF6B6B]" />
                          <span className="text-[10px] font-bold uppercase text-[#9B1E1E]">Rejected</span>
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Row 4: Errors */}
          {(data.errors || []).length > 0 && (
            <details className="nb-card p-2 bg-[#FDFBF7]" data-testid="wa-health-errors">
              <summary className="text-[11px] font-bold cursor-pointer text-[#4A4A4A] uppercase tracking-wider flex items-center gap-1">
                <Warning size={12} weight="bold" /> {data.errors.length} note{data.errors.length > 1 ? "s" : ""} from Meta
              </summary>
              <ul className="mt-2 space-y-1 text-[11px] font-mono">
                {data.errors.map((e, i) => <li key={i} className="text-[#4A4A4A] break-all">• {e}</li>)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const bg = tone === "ok" ? "bg-[#4ECDC4]/20" : tone === "bad" ? "bg-[#FF6B6B]/20" : "bg-white";
  return (
    <div className={`nb-card p-2 ${bg}`}>
      <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">{label}</p>
      <p className="font-heading font-black text-xl leading-none mt-0.5">{value}</p>
    </div>
  );
}
